import {
  Downloading,
  ErrorOutline,
  NoteAddOutlined,
  PublishedWithChangesOutlined,
  RefreshOutlined,
} from "@mui/icons-material";
import TuneRounded from "@mui/icons-material/TuneRounded";
import { Alert, Divider } from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import { green, orange } from "@mui/material/colors";
import isArray from "lodash/isArray";
import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";
import size from "lodash/size";
import { useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import RffSelectField from "../../components/finalForm/inputs/RffSelectField";
import RffSelectMultipleUserEmailField from "../../components/finalForm/inputs/RffSelectMultipleUserEmailField";
import RffSelectUserEmailField from "../../components/finalForm/inputs/RffSelectUserEmailField";
import RffTextField from "../../components/finalForm/inputs/RffTextField";
import RffForm from "../../components/finalForm/RffForm";
import RffGroup from "../../components/finalForm/shared/RffGroup";
import FileUploadField from "../../components/inputs/FileUploadField";
import Flx from "../../components/layout/Flx";
import ScreenContent from "../../components/layout/ScreenContent";
import Htag from "../../components/typography/Htag";
import Txt from "../../components/typography/Txt";
import TitledCard from "../../components/ui/TitledCard/TitledCard";
import { selectOptionsPipelineType } from "../../constants/selectOptions/selectOptionsPipelineType";
import { useAppOrdersEngineRoute } from "../../hooks/useAppOrdersEngineRoute";
import { useEnv } from "../../hooks/useEnv";
import { useUnderwritingHook } from "../../hooks/useUnderwritingHook";
import { loanPipelineClear } from "../../redux/actions/loanPipelineActions";
import BorrowersSection from "./BorrowersSection";
import BorrowingEntitiesSection from "./BorrowingEntitiesSection";
import FileUploadSection from "./FileUploadSection";
import LoanDetailSection from "./LoanDetailSection";
import PropertiesSection from "./PropertiesSection";
import Screen from "../../components/layout/Screen";

// ════════════════════════════════════════════════════════════════
// LoanAppPdfUploadButton
// Steps 1-3 of the import flow:
//   POST /start → SSE progress → GET /result → populate form
// ════════════════════════════════════════════════════════════════

// ════════════════════════════════════════════════════════════════
// LoanQuickIntakeFormScreen
// ════════════════════════════════════════════════════════════════
const LoanAppPdfUploadProcess = ({
  setImportedData,
  importedData,
  setFormData,
}) => {
  const { isResfin } = useEnv();
  // const [progress, setProgress] = useState(true);
  // const [isProcessing, setIsProcessing] = useState("false");
  const [progress, setProgress] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);

  const { loading, startLoanAppImport } = useAppOrdersEngineRoute();
  const user = useSelector((state) => state.user);

  const onUpload = (files) => {
    // const extracted = tempSseReturn?.result?.extracted_loan_data;
    // setFormData(extracted);
    // setImportedData(tempSseReturn);
    // return;

    if (!isArray(files) || isEmpty(files)) return;

    // FileUploadField may return raw File objects or wrapped { file: File, id, name } objects
    const rawItem = files[0];
    const file = rawItem instanceof File ? rawItem : rawItem?.file;

    if (!file || !(file instanceof File)) {
      setError("Invalid file — please try again.");
      return;
    }

    setError(null);
    setIsProcessing(true);
    setProgress({
      step: "uploading",
      message: "Uploading file...",
      percent: 0,
    });

    startLoanAppImport({
      file,
      userEmail: user?.emailAddress,
      pipeline: "Underwriting",
      // bearerToken: "69b97e6137782569c074f7aa-foosball",
      // Step 2: SSE progress updates
      onProgressFn: (progressData) => {
        // console.log("progressData", progressData);
        setProgress(progressData);
      },
      onProcessCompleteFn: (data) => {
        console.log("SSE Process Completed", data);
        const extracted = data?.result?.extracted_loan_data;
        setFormData(extracted);
        setImportedData(data);
      },
      // Step 3: result fetched from GET /result/{instance_id}
      // resultData = parsed workflow result (with Dapr string handling)
      // initialResponse = the original POST /start response (has deal_oid, instance_id)
      onSuccessFn: (resultData, initialResponse) => {
        const extracted = resultData?.extracted_loan_data;
        const dealOid = resultData?.deal_oid || initialResponse?.deal_oid;
        // console.log("onSuccessFn", { resultData, initialResponse });
        if (extracted) {
          setFormData({
            ...extracted,
            // Attach deal_oid so onFormSubmit can route to the OCR create endpoint
            _importDealOid: dealOid,
            // Attach documents & blob keys for Step 4
            _importDocuments: resultData?.documents,
            _importBlobKeys: resultData?.blob_file_keys,
          });
        }

        setIsProcessing(false);

        setProgress(null);
      },

      onCompleteFn: (data) => {
        // console.log("Completed", { data });
        setIsProcessing(false);
      },

      onFailFn: (err) => {
        setError(err?.message || "Something went wrong during import.");
        setIsProcessing(false);
      },
    });
  };

  if (!isResfin) {
    return null;
  }

  if (importedData) {
    const extracted = importedData?.result?.extracted_loan_data;
    const docs = importedData?.result?.documents;

    // Count all non-null leaf values in the extracted data
    const countFields = (obj) => {
      if (isNil(obj)) return 0;
      if (isArray(obj))
        return obj.reduce((sum, item) => sum + countFields(item), 0);
      if (typeof obj === "object") {
        return Object.entries(obj).reduce((sum, [key, val]) => {
          if (key.startsWith("_")) return sum; // skip internal keys
          return sum + countFields(val);
        }, 0);
      }
      return 1; // leaf value
    };

    const fieldCount = countFields(extracted);
    const fileCount = size(docs);

    return (
      <Alert
        color="success"
        icon={
          <PublishedWithChangesOutlined
            sx={{ fontSize: "33px !important", mt: 0.4 }}
          />
        }
        sx={{ border: `1px solid ${green[200]}`, my: 2 }}
      >
        <Flx column gap={2}>
          <Flx ac fw jb>
            <Flx column>
              <Txt bold>Loan Application Data Extracted</Txt>

              <Flx ac g={1}>
                {fieldCount > 0 && (
                  <>
                    <Txt body2 bold sx={{ color: green[800] }}>
                      {fieldCount} fields extracted
                    </Txt>
                    <Txt body2 sx={{ color: green[400] }}>
                      •
                    </Txt>
                  </>
                )}
                {fileCount > 0 && (
                  <Txt body2 bold sx={{ color: green[800] }}>
                    {fileCount} {fileCount === 1 ? "file" : "files"} processed
                  </Txt>
                )}
              </Flx>
            </Flx>

            <Button
              size="small"
              variant="text"
              color="inherit"
              startIcon={
                <RefreshOutlined sx={{ fontSize: "16px !important" }} />
              }
              onClick={() => {
                setImportedData(null);
                setFormData({
                  borrowers: [{}],
                  subjectProperties: [{}],
                  pipeline: "Underwriting",
                });
                setProgress(null);
                setError(null);
              }}
            >
              Clear import & start over
            </Button>
          </Flx>
          <span>
            Please note: The information below was extracted using AI and may
            not be fully accurate. <b>Please review all details carefully</b>{" "}
            before proceeding with loan creation.
          </span>
        </Flx>
      </Alert>
    );
  }

  return (
    <Flx column sx={{ my: 2 }}>
      {!isProcessing && !error && (
        <FileUploadField
          suppressGrid
          sx={{ width: "100%" }}
          onFilesAdded={onUpload}
          suppressFileList
          accept=".pdf"
          borderSize={3}
          minHeight="130px"
          suppressUploadButton
          uploadText="Upload Loan Application PDF"
          uploadTextFontSize="14px"
          uploadTextDefaultOpacity={8.5}
          uploadTextSx={{ fontWeight: 600 }}
          uploadTextBottomContent={
            <Txt secondary sx={{ mt: 0.5, opacity: 0.8 }}>
              Extract data from a loan application and prefil this form
            </Txt>
          }
        />
      )}

      {/* Processing state — show progress bar */}
      {isProcessing && progress && (
        <Flx column gap={4} fw>
          <Alert
            sx={{
              background: "#ffffff",
              // background: grey[100],

              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
              borderRadius: "8px",
              // border: `1px solid ${grey[300]}`,
              ".MuiAlert-message": {
                width: "100%",
              },
            }}
            color="default"
            icon={<Downloading sx={{ fontSize: "26px !important", mt: 0.6 }} />}
          >
            <Flx column gap={0.5}>
              <Flx ac jb g={1} sx={{ mt: 0.2, mb: 0.4 }}>
                <Txt body1 bold>
                  {progress.message || "Processing..."}
                </Txt>
                <Txt body2 color="text.secondary">
                  {`${progress.percent || 0}% complete`}
                </Txt>
              </Flx>
              <Box
                sx={{
                  width: "100%",
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: "#e0e0e0",
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    width: `${progress.percent || 0}%`,
                    height: "100%",
                    backgroundColor: "primary.main",
                    borderRadius: 4,
                    transition: "width 0.4s ease",
                  }}
                />
              </Box>
              <Txt body2 sx={{ mt: 1 }} color="text.secondary">
                Your loan application is being processed. Once complete, please
                review all extracted details for accuracy before submitting.
              </Txt>
            </Flx>
          </Alert>
          <Divider />
        </Flx>
      )}

      {/* Error state */}
      {error && (
        <Flx column gap={2} sx={{ py: 3, px: 1 }}>
          <Txt body1 color="error.main">
            {error}
          </Txt>
          <Button
            variant="outlined"
            onClick={() => {
              setError(null);
              setProgress(null);
            }}
          >
            Try Again
          </Button>
        </Flx>
      )}
    </Flx>
  );
};

const LoanQuickIntakeFormScreen = () => {
  const defaultValues = useMemo(() => {
    return {
      borrowers: [{}],
      subjectProperties: [{}],
      pipeline: "Underwriting",
    };
  }, []);

  const [success, setSuccess] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [formData, setFormData] = useState(null);
  const [importedData, setImportedData] = useState();

  const {
    loading: uwLoading,
    createLoan,
    createLoanWithFileUploads,
  } = useUnderwritingHook();

  // Step 4 endpoint lives on this hook
  const { loading: importLoading, createLoanFromImport } =
    useAppOrdersEngineRoute();

  const loading = uwLoading || importLoading;

  const handleCreateLoanSuccess = useCallback(
    (loanNumber) => {
      resetFormData();

      setSuccess(true);
      dispatch(loanPipelineClear());

      // Guide says: navigate to /loan/{loanNumber} after creation
      if (loanNumber) {
        navigate(`/loan/${loanNumber}`);
      } else {
        navigate("/loans");
      }
    },
    [dispatch, navigate],
  );

  const onFormSubmit = (formData) => {
    let data = { ...formData };

    // Auto-generate loanName from first property address if not set
    let loanName = formData?.loanName;
    if (isNil(loanName) && isArray(data?.subjectProperties)) {
      const propertyName = data?.subjectProperties[0]?.address?.fullAddress;
      if (propertyName) {
        loanName = propertyName;
      }
    }
    data.loanName = loanName;

    // ── Branch: PDF Import flow (Step 4) vs normal create ──
    if (importedData) {
      // Step 4: POST /underwriting/create_loan_from_ocr_app

      createLoanFromImport({
        dealOid: importedData?.result?.deal_oid,
        extractedLoanData: data,
        documents: importedData?.result?.documents,
        blobFileKeys: importedData?.result?.blob_file_keys,
        onSuccessFn: (createdLoan) => {
          handleCreateLoanSuccess(createdLoan?.loanNumber);
        },
        onFailFn: (err) => {
          console.error("Failed to create loan from import:", err);
        },
      });
      return;
    }

    // ── Normal create flow ──
    if (isArray(data?.uploadMetadata) && size(data?.uploadMetadata) >= 1) {
      createLoanWithFileUploads({
        data,
        onSuccessFn: (d) => handleCreateLoanSuccess(d?.loanNumber),
      });
    } else {
      createLoan({
        data,
        onSuccessFn: (d) => handleCreateLoanSuccess(d?.loanNumber),
      });
    }
  };

  const resetFormData = useCallback(() => {
    setFormData(defaultValues);

    setImportedData(null);
  }, [defaultValues]);

  const initialState = useMemo(() => {
    if (isNil(formData)) {
      return defaultValues;
    }
    return formData;
  }, [formData, defaultValues]);

  return (
    <Screen
      sx={{
        pb: 6,
        background: "#f5f8fa",
        // height: "100%",
        overflowY: "auto",
      }}
    >
      <Box
        sx={{
          background: "#ffffff",
          pt: 3,
          pb: 3,
          boxShadow: "0 0 0px 1px #dde0e4",
        }}
      >
        <ScreenContent suppressPaddingTopBottom>
          <Flx column gap={3}>
            <Flx fw jb ac>
              <Flx ac gap={1}>
                <NoteAddOutlined className="thin" />
                <Htag h1>Create Loan</Htag>
              </Flx>
            </Flx>
          </Flx>
        </ScreenContent>
      </Box>
      <RffForm
        className={"required"}
        loading={loading}
        success={success}
        // formSpy
        submitText="Create Loan"
        maxWidth={"md"}
        sx={{
          minHeight: "unset",
          // pb: 6,
          // background: "#f5f8fa",
          // height: "100%",
          // overflowY: "auto",
          // ".titled-group-body-root": {
          //   p: 2.5,
          // },
          // ".titled-group-header-root": {
          //   "h1, svg": {
          //     color: "var(--titleColor)",
          //   },
          // },
          // h4: {
          //   fontWeight: 500,
          //   fontSize: "17px !important",
          // },
        }}
        initialValues={initialState}
        onSubmit={onFormSubmit}
        formTitle={"Create Loan"}
        formDescription="Please provide as much information as possible regarding your loan, borrowers, and properties."
        finalStepLabel="Submit"
      >
        <Flx
          column
          flexGrow={0}
          gap={2}
          sx={{
            p: 2,
            // h1: {
            //   fontSize: "17px",
            // },

            // ".titled-group-header-title-content .MuiSvgIcon-root": {
            //   fontSize: "18px !important",
            // },
          }}
        >
          <LoanAppPdfUploadProcess
            setFormData={setFormData}
            setImportedData={setImportedData}
            importedData={importedData}
          />

          <CreateLoanFormQuestions />
        </Flx>
      </RffForm>
    </Screen>
  );
};

// ════════════════════════════════════════════════════════════════
// Sub-components (unchanged)
// ════════════════════════════════════════════════════════════════

export const CreateLoanFormQuestions = ({
  sx,
  gap = 2,
  cardHeaderFontSize = "17px",
  cardHeaderIconFontSize = "18px",
  defaultExpanded = true,
  mobile = false,
  hideConfigSection = false,
  hideFileUploadSection = false,
}) => {
  return (
    <Flx
      column
      flexGrow={0}
      gap={gap}
      sx={{
        h1: {
          fontSize: cardHeaderFontSize,
        },

        ".titled-group-header-title-content .MuiSvgIcon-root": {
          fontSize: `${cardHeaderIconFontSize} !important`,
        },

        ".titled-group-body-root": {
          p: 2.5,
        },
        ".titled-group-header-root": {
          "h1, svg": {
            color: "var(--titleColor)",
          },
        },
        h4: {
          fontWeight: 500,
          fontSize: "17px !important",
        },
        ...sx,
      }}
    >
      <LoanDetailSection defaultExpanded={defaultExpanded} mobile={mobile} />
      <BorrowersSection defaultExpanded={defaultExpanded} mobile={mobile} />
      <PropertiesSection defaultExpanded={defaultExpanded} mobile={mobile} />
      <BorrowingEntitiesSection
        defaultExpanded={defaultExpanded}
        mobile={mobile}
      />
      {hideFileUploadSection ? null : (
        <FileUploadSection defaultExpanded={defaultExpanded} mobile={mobile} />
      )}
      {hideConfigSection ? null : (
        <TitledCard
          collapsible
          defaultExpanded={defaultExpanded}
          icon={<TuneRounded className="thin" />}
          variant="h1"
          title="Loan Configuration"
        >
          <RffGroup suppressBottomMargin>
            <RffTextField
              name="loanName"
              label="Loan Name"
              helperText="Display name that will be visible in the loan pipeline table"
            />
            <RffSelectField
              name="pipeline"
              label="Select Pipeline"
              size={6}
              required
              options={selectOptionsPipelineType}
            />
            <RffSelectUserEmailField
              size={6}
              name="salesperson"
              required
              label="Assigned Salesperson"
            />
            <RffSelectMultipleUserEmailField
              name="permittedUsers"
              label="Permitted Users On Loan"
            />
          </RffGroup>
        </TitledCard>
      )}
    </Flx>
  );
};

// =-======================================-=
// const LoanAppPdfUploadButton = ({ setImportedData, setFormData }) => {
//   const [show, setShow] = useState(false);
//   const [progress, setProgress] = useState(null);
//   const [error, setError] = useState(null);
//   const [isProcessing, setIsProcessing] = useState(false);

//   const { loading, startLoanAppImport } = useAppOrdersEngineRoute();
//   const user = useSelector((state) => state.user);

//   const handleClose = () => {
//     if (isProcessing) return;
//     setShow(false);
//     setProgress(null);
//     setError(null);
//   };

//   const onUpload = (files) => {
//     if (!isArray(files) || isEmpty(files)) return;

//     // FileUploadField may return raw File objects or wrapped { file: File, id, name } objects
//     const rawItem = files[0];
//     const file = rawItem instanceof File ? rawItem : rawItem?.file;

//     if (!file || !(file instanceof File)) {
//       setError("Invalid file — please try again.");
//       return;
//     }

//     setError(null);
//     setIsProcessing(true);
//     setProgress({
//       step: "uploading",
//       message: "Uploading file...",
//       percent: 0,
//     });

//     startLoanAppImport({
//       file,
//       userEmail: user?.emailAddress,
//       pipeline: "Underwriting",
//       // bearerToken: "69b97e6137782569c074f7aa-foosball",
//       // Step 2: SSE progress updates
//       onProgressFn: (progressData) => {
//         // console.log("progressData", progressData);
//         setProgress(progressData);
//       },
//       onProcessCompleteFn: (data) => {
//         console.log("SSE Process Completed", data);
//         const extracted = data?.result?.extracted_loan_data;
//         setFormData(extracted);
//         setImportedData(data);
//       },
//       // Step 3: result fetched from GET /result/{instance_id}
//       // resultData = parsed workflow result (with Dapr string handling)
//       // initialResponse = the original POST /start response (has deal_oid, instance_id)
//       onSuccessFn: (resultData, initialResponse) => {
//         const extracted = resultData?.extracted_loan_data;
//         const dealOid = resultData?.deal_oid || initialResponse?.deal_oid;
//         // console.log("onSuccessFn", { resultData, initialResponse });
//         if (extracted) {
//           setFormData({
//             ...extracted,
//             // Attach deal_oid so onFormSubmit can route to the OCR create endpoint
//             _importDealOid: dealOid,
//             // Attach documents & blob keys for Step 4
//             _importDocuments: resultData?.documents,
//             _importBlobKeys: resultData?.blob_file_keys,
//           });
//         }

//         setIsProcessing(false);
//         setShow(false);
//         setProgress(null);
//       },

//       onCompleteFn: (data) => {
//         // console.log("Completed", { data });
//         setIsProcessing(false);
//       },

//       onFailFn: (err) => {
//         setError(err?.message || "Something went wrong during import.");
//         setIsProcessing(false);
//       },
//     });
//   };

//   return (
//     <>
//       <Button
//         size="large"
//         startIcon={<UploadFileOutlined />}
//         onClick={() => setShow(true)}
//       >
//         Import From Loan Application PDF
//       </Button>
//       <BasicModal
//         onClose={handleClose}
//         maxWidth="sm"
//         show={show}
//         title="Import from Loan Application PDF"
//       >
//         {/* Upload state — show file drop zone */}
//         {!isProcessing && !error && (
//           <FileUploadField
//             suppressGrid
//             sx={{ width: "100%" }}
//             onFilesAdded={onUpload}
//             suppressFileList
//             accept=".pdf"
//             borderSize={3}
//             minHeight="100px"
//             suppressUploadButton
//             uploadText="Upload Loan Application PDF"
//           />
//         )}

//         {/* Processing state — show progress bar */}
//         {isProcessing && progress && (
//           <Flx column gap={2} sx={{ py: 3, px: 1 }}>
//             <Txt body1 bold>
//               {progress.message || "Processing..."}
//             </Txt>
//             <Box
//               sx={{
//                 width: "100%",
//                 height: 8,
//                 borderRadius: 4,
//                 backgroundColor: "#e0e0e0",
//                 overflow: "hidden",
//               }}
//             >
//               <Box
//                 sx={{
//                   width: `${progress.percent || 0}%`,
//                   height: "100%",
//                   backgroundColor: "primary.main",
//                   borderRadius: 4,
//                   transition: "width 0.4s ease",
//                 }}
//               />
//             </Box>
//             <Txt body2 color="text.secondary">
//               {progress.step === "uploading"
//                 ? "Uploading..."
//                 : `${progress.percent || 0}% complete`}
//             </Txt>
//           </Flx>
//         )}

//         {/* Error state */}
//         {error && (
//           <Flx column gap={2} sx={{ py: 3, px: 1 }}>
//             <Txt body1 color="error.main">
//               {error}
//             </Txt>
//             <Button
//               variant="outlined"
//               onClick={() => {
//                 setError(null);
//                 setProgress(null);
//               }}
//             >
//               Try Again
//             </Button>
//           </Flx>
//         )}
//       </BasicModal>
//     </>
//   );
// };

// const DownloadLoanUploadTemplate = () => {
//   const { loading, downloadLoanUploadTemplate } = useLoanFilesHook();

//   const onClick = () => {
//     downloadLoanUploadTemplate({});
//   };
//   return (
//     <Button
//       loading={loading}
//       fullWidth={false}
//       variant="text"
//       onClick={onClick}
//       startIcon={<DownloadOutlined />}
//     >
//       Download Excel Template
//     </Button>
//   );
// };

// const UploadExcelContent = ({ setFormData }) => {
//   const { loading, populateLoanFormFromExcel } = useUnderwritingHook();

//   const onUpload = (excel) => {
//     populateLoanFormFromExcel({
//       excel: excel[0],
//       onSuccessFn: (data) => {
//         setFormData(data);
//       },
//     });
//   };
//   return (
//     <>
//       <Box sx={{ pt: 2 }}>
//         <Flx fw jb ac>
//           <Txt>Quick Intake Excel Template Upload</Txt>
//           <DownloadLoanUploadTemplate />
//         </Flx>
//         <FileUploadField
//           suppressGrid
//           sx={{ width: "100%" }}
//           onFilesAdded={onUpload}
//           suppressFileList
//           loading={loading}
//           borderSize={3}
//           minHeight="100px"
//           suppressUploadButton
//           uploadText={"Upload Quick Intake Loan Template Excel"}
//         />
//       </Box>
//     </>
//   );
// };

// const TemplatesRenderer = ({ saves, onUpdate }) => {
//   if (isEmpty(saves)) {
//     return null;
//   }

//   return (
//     <Flx ac gap={1}>
//       <Txt sx={{ flexShrink: 0 }}>Saved Templates:</Txt>
//       <LocalStorageOptionRenderer
//         saves={saves}
//         lsPrefix={lsPrefix}
//         onUpdate={onUpdate}
//       />
//     </Flx>
//   );
// };

export default LoanQuickIntakeFormScreen;
